import debug from 'debug';
import * as actionTypes from "./actionTypes";
import auth from "../../api/auth";
import userService from "../../api/userService";
import tokenStorage from "../../utils/tokenStorage";
import { clearTokenCache } from "../../auth/session";
import sEmitter from 'storage-emitter'

// Create debug namespaces for login actions
const debugLogin = debug('auth:login');
const debugSession = debug('auth:session');

export const tokenExpirationTime = () => {
    const authData = auth.getAuthData();
    if (!authData) return 0;
    
    // Calculate remaining time until refresh token expires
    // The session should expire when the refresh token expires, not the access token
    const refreshMs = Number(authData.refresh);
    if (Number.isFinite(refreshMs)) {
        const remainingTime = refreshMs - Date.now();
        debugSession('Remaining Refresh Token Time: %dms (%d minutes)', remainingTime, Math.floor(remainingTime / 60000));
        return Math.max(0, remainingTime);
    }
    
    // Fallback: if no refresh timestamp, calculate remaining time from access token exp
    // This occurs when refresh_exp claim is missing from JWT (legacy tokens or backend compatibility)
    const expMs = Number(authData.exp);
    const createdMs = Number(authData.created);
    if (Number.isFinite(expMs) && Number.isFinite(createdMs)) {
        const remainingTime = expMs - Date.now();
        debugSession('Remaining time (fallback via exp): %dms', remainingTime);
        return Math.max(0, remainingTime);
    }
    
    // Last resort: return 0 if we can't determine expiration
    debugSession('Cannot determine token expiration time');
    return 0;
};

const warningBefore = 30 * 1000; //30 seconds *1000 ms

export const passwordExpiredAction = (userName) => {
    debugLogin('Password expired for user: %s', userName);
    tokenStorage.clear();
    return {
        type: actionTypes.USER_PASSWORD_EXPIRED,
        userName
    };
};

export const profileUpdated = (userInfo) => {
    
    return {type: actionTypes.USER_PROFILE_UPDATED, userInfo: userInfo};
};

export const restoreUserLogin = (isImpersonation) => async (dispatch) => {

    debugLogin('Restore login attempt started, isImpersonation: %s', isImpersonation);
    dispatch({ type: actionTypes.RESTORE_USER_LOGIN_STARTED });
    const authData = auth.getAuthData();

    if (!authData) {
        debugLogin('Restore login failed: No auth data found');
        dispatch({ type: actionTypes.RESTORE_USER_LOGIN_FAILED });
        return false;
    } else {
        debugLogin('Auth data found, userId: %s', authData.userId);
        await afterLoginCheck(authData, dispatch, isImpersonation);
        dispatch({type: actionTypes.RESTORE_USER_LOGIN, authData: authData});

        const expirationTime = tokenExpirationTime();
        if (expirationTime > 0) {

            debugLogin('Token restored successfully! remaining refresh window: %dms', expirationTime);
            dispatch(_checkAuthTimeout(expirationTime));
        } else {
            debugLogin('Token expired or missing refresh expiry, logging out');
            dispatch(logout());
        }
        return true;
    }
};
const afterLoginCheck =async (authData, dispatch,isImpersonation)=>{

    debugLogin('After login check started for userId: %s, isImpersonation: %s', authData.userId, isImpersonation);
    //const isProfileValid = await userService.checkUserData(authData.userId);
    //authData.profileIsNotComplete =!isProfileValid;


    const result = await userService.checkUserData(authData.userId);
    //authData.profileIsNotComplete = !isProfileValid;

    authData.IsSecurityQuesionsValid = result.IsSecurityQuesionsValid;
    authData.IsUserProfileValid = result.IsUserProfileValid;
    debugLogin('User data check completed: IsSecurityQuesionsValid=%s, IsUserProfileValid=%s',
        result.IsSecurityQuesionsValid, result.IsUserProfileValid);

    const accounts = await userService.getAccounts(authData.userId);
    debugLogin('Fetched %d accounts for user', accounts?.length || 0);

    if (isImpersonation) {
        debugLogin('Skipping account selection for impersonation');
        return;
    }

    if(accounts && accounts.length !== 0){

        const defaultAccount = accounts.find(a=>a.data?.IsDefault === true);
        // console.log("%c Default Account Found", 'color: lime;background:black;' ,defaultAccount);
        const accountToSelect = defaultAccount ?? accounts[0];
        debugLogin('Account selected: %s (isDefault: %s)',
            accountToSelect?.value || accountToSelect?.AccountNumber,
            !!defaultAccount);
        // console.log("Account to Select : ", accountToSelect);
        dispatch(accountSelected(accountToSelect));
    }

}

export const accountSelected = (account)=>{
    
    return {type: actionTypes.ACCOUNT_SELECTED, account};
}


export const requestLogin = (userName, password, isWindowsAuth = true) => async(dispatch) => {

    debugLogin('Login attempt started for user: %s, isWindowsAuth: %s', userName, isWindowsAuth);
    dispatch(_requestUserLogin());
    try {
        await auth.login(userName, password, isWindowsAuth);
        const authData = auth.getAuthData();
        debugLogin('Login successful, userId: %s', authData?.userId);

        await afterLoginCheck(authData, dispatch);

        dispatch(_receiveUserLogin(authData));
        dispatch(_checkAuthTimeout(tokenExpirationTime()));
        debugLogin('Login completed, session timer set');

    } catch (e) {
        debugLogin('Login failed: %o', e.response?.data || e.message);
        const isPasswordExpired =
            e?.response?.status === 400 &&
            e?.response?.data?.error_description === 'The user password is expired.';

        if (isPasswordExpired) {
            debugLogin('Password expired detected for user: %s', userName);
            console.warn('[Login Action] User password is expired', { userName });
            dispatch(passwordExpiredAction(userName));
            return;
        }
        dispatch(_failedUserLogin(e.response));
    }
};

const _requestUserLogin = () => {
    return {type: actionTypes.REQUEST_USER_LOGIN};
}
const _receiveUserLogin = (authData) => {
    return {type: actionTypes.RECEIVE_USER_LOGIN, authData: authData};
}

const _failedUserLogin = (response) => {
    
    return {type: actionTypes.FAILED_USER_LOGIN, serverResponse: response};
}

export const refreshToken = () => async(dispatch) => {

    debugLogin('Token refresh requested');
    dispatch({type: actionTypes.REQUEST_REFRESH_TOKEN});
    try {
        const tokenInfo = await auth.refreshToken();
        const authData = auth.getAuthData(tokenInfo.access_token);
        debugLogin('Token refresh successful, new expiration time: %dms', tokenExpirationTime());

        dispatch({type: actionTypes.RECEIVE_REFRESH_TOKEN, authData: authData});
        dispatch(_checkAuthTimeout(tokenExpirationTime()));

    } catch (e) {
        debugLogin('Token refresh failed: %o', e.response?.data || e.message);
        dispatch({type: actionTypes.FAILED_REFRESH_TOKEN, serverResponse: e.response});
    }

};

//impoersonation
export const impersonate = (refreshToken,accountNumber) => async(dispatch) => {

    debugLogin('REQUEST_IMPERSONATE, ACCOUNTNUMBER: %s', accountNumber);
    dispatch({type: actionTypes.REQUEST_IMPERSONATE});
    try {
        await auth.impersonateByRefreshToken(refreshToken, 'testuser'); 
        dispatch({type: actionTypes.SUCCESS_IMPERSONATE});        
        debugLogin('SUCCESS_IMPERSONATE');
      
        if (accountNumber) {           
            dispatch({ type: actionTypes.ACCOUNT_SELECTED, account: { value:accountNumber } });
        }

    } catch (e) {
        dispatch({type: actionTypes.FAILED_IMPERSONATE, serverResponse: e.response});
    }



};
export const passwordChangedAction = () => {
    return {type: actionTypes.USER_PASSWORD_CHANGED};
};

export const profilePageOpened = () => {
    return {type: actionTypes.USER_PROFILE_IS_NOT_COMPLETED_REDIRECTED};
};

export const logout = () => (dispatch, getState) => {
    const stack = new Error().stack;
    const caller = stack?.split('\n')[2]?.trim() || 'unknown';
    const fullStack = stack?.split('\n').slice(1, 6).map(line => line.trim()).join(' > ') || 'unknown';

    debugLogin('⚠️ LOGOUT ACTION INITIATED', {
        caller,
        fullStack,
        timestamp: new Date().toISOString()
    });
    console.warn('[Login Action] LOGOUT INITIATED', {
        caller,
        fullStack,
        timestamp: new Date().toISOString()
    });

    const oldTimerId = getState().login.sessionTimerId;
    if (oldTimerId) {
        debugSession('Clearing session timer: %s', oldTimerId);
        clearTimeout(oldTimerId);
    }

    debugLogin('Emitting storage emitter logout event');
    sEmitter.emit('logout');
    
    // 🔒 Security: Clear in-memory token cache before clearing storage
    debugLogin('Securely clearing in-memory token cache');
    clearTokenCache();
    tokenStorage.clear();
    debugLogin('Token storage and cache cleared, logout event emitted');

    dispatch({type: actionTypes.USER_LOGOUT});
};

const _startSessionTimer = (timerId) => {
    return {type: actionTypes.START_SESSION_TIMER, sessionTimerId: timerId};
};

const _sessionExpirationWarn = () => {
    return {type: actionTypes.SESSION_EXPIRATION_WARNING};
};

export const _checkAuthTimeout = (refreshTokenExpirationTime) => {
    // const refreshTokenExpirationTime = 600000; //10 min *60sec * 1000ms     const
    // refreshTokenExpirationTime = 30000; //10 min *60sec * 1000ms const   const
    // warningDialogTime = 5000; //30 seconds *1000 ms
    const timerValue = refreshTokenExpirationTime - warningBefore;
    
    debugSession('Session timer set for %dms (warning %ds before expiration)', timerValue, warningBefore / 1000);

    return (dispatch, getState) => {

        const oldTimerId = getState().login.sessionTimerId;
        if (oldTimerId) {
            clearTimeout(oldTimerId);
        }

        var id = setTimeout(() => {
            debugSession('Session expiration warning triggered');
            dispatch(_sessionExpirationWarn());
        }, timerValue);
        dispatch(_startSessionTimer(id));
    };
};


//export const sessionTransfered = () => {
//    return { type: actionTypes.SESSION_TRANSFERED };
//}
